import { Check, ChevronDown, Loader2, Search, Wrench, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { AgentAccessTab } from "@/components/agents/AgentAccessTab";
import { AgentFormFields } from "@/components/agents/AgentFormFields";
import { AGENT_MODAL_TABS, AgentModalShell, type AgentModalTab } from "@/components/agents/AgentModalShell";
import { resolveExtensionIcon } from "@/components/extensions/IconColorPickers";
import { AgentProjectNotice } from "@/components/shared";
import { formatRoutingKeywords, parseRoutingKeywords, sameRoutingKeywords } from "@/lib/routingKeywords";
import { agentService } from "@/lib/services";
import type {
  Agent,
  AgentSkill,
  AgentSkillAttach,
  AgentToolConfig,
  AgentUpdate,
  DataCollection,
  ExtensionBadge,
  ModelTierDefault,
  SkillSummary,
  ToolSummary,
} from "@/types";
import type { AIModel } from "@/types/models";

// ============================================================================
// Capabilities Selection Component (unified Skills + Core Tools)
// ============================================================================

interface CapabilitiesSelectionProps {
  availableSkills: SkillSummary[];
  availableTools: ToolSummary[];
  selectedSkills: AgentSkillAttach[];
  selectedToolConfigs: AgentToolConfig[];
  onSkillsChange: (skills: AgentSkillAttach[]) => void;
  onToolsChange: (toolConfigs: AgentToolConfig[]) => void;
}

interface ExtensionGroup {
  extension: ExtensionBadge;
  skills: SkillSummary[];
  tools: ToolSummary[];
}

export function CapabilitiesSelection({
  availableSkills,
  availableTools,
  selectedSkills,
  selectedToolConfigs,
  onSkillsChange,
  onToolsChange,
}: Readonly<CapabilitiesSelectionProps>) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showOtherTools, setShowOtherTools] = useState(false);

  // Platform-type extensions (File Search, Code Execution, Document Tools,
  // Task Orchestration, etc.) are baseline capabilities every agent has
  // automatically — they're not offered here as an opt-in choice.
  const addOnSkills = availableSkills.filter((skill) => skill.extension?.extensionType !== "platform");
  const addOnTools = availableTools.filter((tool) => tool.extension?.extensionType !== "platform");

  // Tools that are required by a visible skill don't need their own card —
  // attaching the skill auto-provisions them. Computed from the full
  // (unfiltered) set so this holds regardless of the current search query.
  const skillCoveredToolUuids = new Set<string>();
  for (const skill of addOnSkills) {
    for (const uuid of skill.requiredToolUuids) {
      skillCoveredToolUuids.add(uuid);
    }
  }

  const lowerQuery = searchQuery.toLowerCase();
  const filteredSkills = addOnSkills.filter(
    (skill) =>
      skill.name.toLowerCase().includes(lowerQuery) ||
      (skill.description?.toLowerCase().includes(lowerQuery) ?? false) ||
      (skill.category?.toLowerCase().includes(lowerQuery) ?? false),
  );
  const filteredTools = addOnTools.filter(
    (tool) =>
      !skillCoveredToolUuids.has(tool.uuid) &&
      (tool.name.toLowerCase().includes(lowerQuery) || (tool.description?.toLowerCase().includes(lowerQuery) ?? false)),
  );

  const isSkillSelected = (uuid: string) => selectedSkills.some((s) => s.skillUuid === uuid);
  const isToolSelected = (uuid: string) => selectedToolConfigs.some((t) => t.toolUuid === uuid);

  const toggleSkill = (skill: SkillSummary) => {
    if (isSkillSelected(skill.uuid)) {
      onSkillsChange(selectedSkills.filter((s) => s.skillUuid !== skill.uuid));
    } else {
      onSkillsChange([...selectedSkills, { skillUuid: skill.uuid, isEnabled: true, priority: selectedSkills.length }]);
    }
  };

  const toggleTool = (tool: ToolSummary) => {
    if (isToolSelected(tool.uuid)) {
      onToolsChange(selectedToolConfigs.filter((t) => t.toolUuid !== tool.uuid));
    } else {
      onToolsChange([...selectedToolConfigs, { toolUuid: tool.uuid, isEnabled: true }]);
    }
  };

  // Group capabilities that belong to an Extension together, by extension.
  // Anything without an extension falls back to the existing category/other-tools UX below.
  const extensionGroups = new Map<string, ExtensionGroup>();
  for (const skill of filteredSkills) {
    if (!skill.extension) continue;
    const key = skill.extension.uuid;
    if (!extensionGroups.has(key)) extensionGroups.set(key, { extension: skill.extension, skills: [], tools: [] });
    extensionGroups.get(key)!.skills.push(skill);
  }
  for (const tool of filteredTools) {
    if (!tool.extension) continue;
    const key = tool.extension.uuid;
    if (!extensionGroups.has(key)) extensionGroups.set(key, { extension: tool.extension, skills: [], tools: [] });
    extensionGroups.get(key)!.tools.push(tool);
  }
  const sortedExtensionGroups = Array.from(extensionGroups.values()).sort((a, b) =>
    a.extension.name.localeCompare(b.extension.name),
  );

  const ungroupedSkills = filteredSkills.filter((skill) => !skill.extension);
  const ungroupedTools = filteredTools.filter((tool) => !tool.extension);

  // Group ungrouped skills by category (unchanged from prior behavior)
  const categories = new Map<string, SkillSummary[]>();
  for (const skill of ungroupedSkills) {
    const cat = skill.category || "Other";
    if (!categories.has(cat)) categories.set(cat, []);
    categories.get(cat)!.push(skill);
  }

  const renderSkillCard = (skill: SkillSummary, blocked: boolean) => {
    const selected = isSkillSelected(skill.uuid);
    return (
      <button
        key={skill.uuid}
        type="button"
        onClick={() => toggleSkill(skill)}
        disabled={blocked}
        title={blocked ? `${skill.extension?.name ?? "This extension"} is disabled` : undefined}
        className={`flex items-center gap-3 p-3 rounded-lg border transition text-left ${
          blocked
            ? "bg-surface-secondary/40 border-border-secondary opacity-50 cursor-not-allowed"
            : selected
              ? "bg-amber-500/10 border-amber-500/30"
              : "bg-surface-secondary hover:bg-interactive-active border-border-secondary"
        }`}
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${skill.color || "#F59E0B"}20` }}
        >
          <Zap className="w-4 h-4" style={{ color: skill.color || "#F59E0B" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-content-primary text-sm font-medium truncate">{skill.name}</div>
          <div className="text-content-tertiary text-xs truncate">{skill.description}</div>
        </div>
        {selected && <Check className="w-4 h-4 text-amber-400 shrink-0" />}
      </button>
    );
  };

  const renderToolCard = (tool: ToolSummary, blocked: boolean, compact: boolean) => {
    const selected = isToolSelected(tool.uuid);
    const title = blocked ? `${tool.extension?.name ?? "This extension"} is disabled` : undefined;
    const stateClasses = blocked
      ? "bg-surface-secondary/40 border-border-secondary opacity-50 cursor-not-allowed"
      : selected
        ? "bg-brand-primary-subtle border-brand-primary/30"
        : "bg-surface-secondary hover:bg-interactive-active border-border-secondary";

    if (compact) {
      return (
        <button
          key={tool.uuid}
          type="button"
          onClick={() => toggleTool(tool)}
          disabled={blocked}
          title={title}
          className={`flex items-center gap-2 p-2 rounded-lg border transition text-left ${stateClasses}`}
        >
          <div className="w-6 h-6 rounded-sm flex items-center justify-center shrink-0 bg-brand-primary-subtle">
            <Wrench className="w-3 h-3 text-brand-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-content-primary text-xs font-medium truncate">{tool.name}</div>
            <div className="text-content-quaternary text-[10px] truncate">{tool.description}</div>
          </div>
          {selected && <Check className="w-3 h-3 text-brand-primary shrink-0" />}
        </button>
      );
    }

    return (
      <button
        key={tool.uuid}
        type="button"
        onClick={() => toggleTool(tool)}
        disabled={blocked}
        title={title}
        className={`flex items-center gap-3 p-3 rounded-lg border transition text-left ${stateClasses}`}
      >
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-brand-primary-subtle">
          <Wrench className="w-4 h-4 text-brand-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-content-primary text-sm font-medium truncate">{tool.name}</div>
          <div className="text-content-tertiary text-xs truncate">{tool.description}</div>
        </div>
        {selected && <Check className="w-4 h-4 text-brand-primary shrink-0" />}
      </button>
    );
  };

  const hasAnySkillResults = sortedExtensionGroups.length > 0 || ungroupedSkills.length > 0;

  return (
    <div className="space-y-5">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search capabilities..."
          className="w-full pl-9 pr-4 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary text-sm"
        />
      </div>

      {/* Transparency note: baseline capabilities are automatic, not shown below */}
      <p className="text-xs text-content-quaternary bg-surface-secondary/60 rounded-lg px-3 py-2">
        Every agent automatically includes baseline capabilities like file search, code execution, and document
        creation. The options below are additional integrations you can turn on for this agent.
      </p>

      {/* Capabilities grouped by parent Extension (e.g. HubSpot, SharePoint) */}
      {sortedExtensionGroups.map(({ extension, skills, tools }) => {
        const ExtIcon = resolveExtensionIcon(extension.icon);
        const extColor = extension.color || "#64748B";
        return (
          <div key={extension.uuid}>
            <div className="flex items-center gap-2 mb-2">
              <div
                className="w-5 h-5 rounded flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${extColor}20` }}
              >
                <ExtIcon className="w-3 h-3" style={{ color: extColor }} />
              </div>
              <h4 className="text-xs font-semibold text-content-tertiary uppercase tracking-wider">{extension.name}</h4>
              {!extension.isEnabled && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium bg-red-500/10 text-red-400 border border-red-500/20 rounded">
                  Disabled
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {skills.map((skill) => renderSkillCard(skill, !extension.isEnabled && !isSkillSelected(skill.uuid)))}
              {tools.map((tool) => renderToolCard(tool, !extension.isEnabled && !isToolSelected(tool.uuid), false))}
            </div>
          </div>
        );
      })}

      {/* Skills without a parent Extension, grouped by category */}
      {Array.from(categories.entries()).map(([category, skills]) => (
        <div key={category}>
          <h4 className="text-xs font-semibold text-content-tertiary uppercase tracking-wider mb-2">{category}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {skills.map((skill) => renderSkillCard(skill, false))}
          </div>
        </div>
      ))}

      {!hasAnySkillResults && !searchQuery && (
        <p className="text-content-quaternary text-sm text-center py-4">No add-on capabilities available.</p>
      )}
      {!hasAnySkillResults && searchQuery && !showOtherTools && (
        <p className="text-content-quaternary text-sm text-center py-4">No matching capabilities found.</p>
      )}

      {/* Other Tools — collapsible (tools without a parent Extension or skill) */}
      <div className="border-t border-border-primary pt-4">
        <button
          type="button"
          onClick={() => setShowOtherTools(!showOtherTools)}
          className="flex items-center gap-2 text-sm font-medium text-content-secondary hover:text-content-primary transition w-full text-left"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${showOtherTools ? "" : "-rotate-90"}`} />
          <Wrench className="w-4 h-4" />
          Other Tools
          {selectedToolConfigs.length > 0 && (
            <span className="ml-auto px-1.5 py-0.5 text-xs bg-brand-primary-subtle text-brand-primary rounded-sm">
              {selectedToolConfigs.length}
            </span>
          )}
        </button>
        {showOtherTools && (
          <div className="mt-3 grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
            {ungroupedTools.map((tool) => renderToolCard(tool, false, true))}
            {ungroupedTools.length === 0 && (
              <div className="col-span-2 text-center py-4 text-content-quaternary text-sm">
                {searchQuery ? "No matching tools found" : "No other tools available"}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Edit Agent Modal with Tabs
// ============================================================================

export interface EditAgentModalProps {
  agent: Agent;
  onClose: () => void;
  onSave: (data: AgentUpdate) => Promise<void>;
  models: AIModel[];
  tierDefaults?: ModelTierDefault[];
  dataSources: DataCollection[];
  availableSkills: SkillSummary[];
  availableTools: ToolSummary[];
}

export function EditAgentModal({
  agent,
  onClose,
  onSave,
  models,
  tierDefaults = [],
  dataSources,
  availableSkills,
  availableTools,
}: Readonly<EditAgentModalProps>) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<AgentModalTab>("general");
  const [agentSkills, setAgentSkills] = useState<AgentSkill[]>([]);
  const [isLoadingSkills, setIsLoadingSkills] = useState(true);
  const [formData, setFormData] = useState({
    name: agent.name,
    description: agent.description || "",
    color: agent.color || "#EC622B",
    systemPrompt: agent.systemPrompt || "",
    modelId: agent.modelId,
    teamUuid: agent.teamUuid || "",
    dataCollectionIds: agent.dataCollectionIds || [],
    routingKeywords: formatRoutingKeywords(agent.settings?.routing_keywords),
  });
  const [selectedSkills, setSelectedSkills] = useState<AgentSkillAttach[]>([]);
  const [selectedToolConfigs, setSelectedToolConfigs] = useState<AgentToolConfig[]>(
    // Initialize from agent's existing tools (tools come back as ToolSummary[]).
    // "Core" platform-type tools are excluded: they're implicit/always-on and
    // no longer shown as a selectable option in the Capabilities picker, so
    // including them here would inflate the tab badge count and (since
    // toolConfigs fully replaces AgentTool links on save) keep re-persisting
    // stale legacy attachments instead of letting them fall away.
    (agent.tools || [])
      .filter((t): t is ToolSummary & { uuid: string } => "uuid" in t && !!t.uuid)
      .filter((t) => t.extension?.extensionType !== "platform")
      .map((t) => ({
        toolUuid: t.uuid,
        isEnabled: true,
      })),
  );

  // Load agent skills on mount
  useEffect(() => {
    const loadAgentSkills = async () => {
      try {
        setIsLoadingSkills(true);
        const response = await agentService.listSkills(agent.uuid);
        setAgentSkills(response.data);
        setSelectedSkills(
          response.data
            // Exclude "core" platform-type skills for the same reason as tools
            // above — they're implicit now, not a selectable capability.
            .filter((as) => as.skill.extension?.extensionType !== "platform")
            .map((as) => ({
              skillUuid: as.skill.uuid,
              config: as.config,
              isEnabled: as.isEnabled,
              priority: as.priority,
            })),
        );
      } catch (err) {
        console.error("Failed to load agent skills:", err);
      } finally {
        setIsLoadingSkills(false);
      }
    };
    loadAgentSkills();
  }, [agent.uuid]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const routingKeywords = parseRoutingKeywords(formData.routingKeywords);
      const keywordsChanged = !sameRoutingKeywords(
        routingKeywords,
        parseRoutingKeywords(formatRoutingKeywords(agent.settings?.routing_keywords)),
      );
      // First save agent basic info (including tool configs)
      await onSave({
        name: formData.name,
        description: formData.description,
        color: formData.color,
        systemPrompt: formData.systemPrompt,
        // Only a model picked here is sent, so a form opened before an admin moved the agent can't move it back.
        modelId: formData.modelId === agent.modelId ? undefined : formData.modelId,
        teamUuid: formData.teamUuid,
        dataCollectionIds: formData.dataCollectionIds,
        toolConfigs: selectedToolConfigs,
        // Only a keyword edit sends settings: the backend replaces the whole dict and versions it (MHS-348).
        ...(keywordsChanged ? { settings: { ...agent.settings, routing_keywords: routingKeywords } } : {}),
      });

      // Then update skills if there are any changes
      const currentSkillUuids = new Set(agentSkills.map((as) => as.skill.uuid));
      const newSkillUuids = new Set(selectedSkills.map((s) => s.skillUuid));

      // Skills to detach
      const skillsToDetach = agentSkills.filter((as) => !newSkillUuids.has(as.skill.uuid));
      for (const as of skillsToDetach) {
        await agentService.detachSkill(agent.uuid, as.skill.uuid);
      }

      // Skills to attach or update
      const skillsToAttachOrUpdate = selectedSkills.filter(
        (s) =>
          !currentSkillUuids.has(s.skillUuid) ||
          agentSkills.some(
            (as) => as.skill.uuid === s.skillUuid && (as.priority !== s.priority || as.isEnabled !== s.isEnabled),
          ),
      );

      if (skillsToAttachOrUpdate.length > 0) {
        await agentService.attachSkills(agent.uuid, skillsToAttachOrUpdate);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AgentModalShell
      title="Edit Agent"
      formId="edit-agent-form"
      activeTab={activeTab}
      onTabChange={setActiveTab}
      capabilitiesBadgeCount={selectedSkills.length + selectedToolConfigs.length}
      onClose={onClose}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      isValid={!!formData.name && !!formData.systemPrompt}
      submitLabel="Save Changes"
      tabs={[AGENT_MODAL_TABS.general, AGENT_MODAL_TABS.capabilities, AGENT_MODAL_TABS.access]}
    >
      <AgentProjectNotice agent={agent} showVisibilityHint className="mb-4" />

      {activeTab === "general" && (
        <AgentFormFields
          idPrefix="edit-agent"
          formData={formData}
          onChange={setFormData}
          models={models}
          tierDefaults={tierDefaults}
          dataSources={dataSources}
        />
      )}

      {activeTab === "capabilities" &&
        (isLoadingSkills ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
          </div>
        ) : (
          <CapabilitiesSelection
            availableSkills={availableSkills}
            availableTools={availableTools}
            selectedSkills={selectedSkills}
            selectedToolConfigs={selectedToolConfigs}
            onSkillsChange={setSelectedSkills}
            onToolsChange={setSelectedToolConfigs}
          />
        ))}

      {activeTab === "access" && <AgentAccessTab agentUuid={agent.uuid} />}
    </AgentModalShell>
  );
}
